// Global Instructions Rule Applied!

import { useCallback } from 'react'
import { FieldTypeEnum, InputMode } from '../types.ts'

export interface UseInputManagerFieldProps {
  config: {
    type: FieldTypeEnum
    editable?: boolean
    value: any
  }
  inputMode: InputMode
  tempValue: any
  onStartEdit: () => void
  onConfirm: (overrideValue?: any) => void
  onCancel: () => void
  onValueChange: (value: any) => void
  onBlur?: () => void
  onFieldEdit?: (isEditing: boolean) => void
  updateTempValue: (newValue: any, originalValue: any) => void
  resetField: (originalValue: any) => void
  setShowPopconfirm: (show: boolean) => void
  hasChanges: () => boolean
}

export interface UseInputManagerFieldReturn {
  handleStartEdit: () => void
  handleConfirm: (overrideValue?: any) => void
  handleCancel: () => void
  handleValueChange: (newValue: any) => void
  handleKeyPress: (e: React.KeyboardEvent) => void
  handleBlurInternal: () => void
  handlePopconfirmConfirm: () => void
  handlePopconfirmCancel: () => void
}

export function useInputManagerField({
  config,
  inputMode,
  tempValue,
  onStartEdit,
  onConfirm,
  onCancel,
  onValueChange,
  onBlur,
  onFieldEdit,
  updateTempValue,
  resetField,
  setShowPopconfirm,
  hasChanges
}: UseInputManagerFieldProps): UseInputManagerFieldReturn {
  const handleStartEdit = useCallback(() => {
    if (config.editable === false) return
    onStartEdit()
    onFieldEdit?.(true)
  }, [config.editable, onStartEdit, onFieldEdit])

  const handleConfirm = useCallback(
    (overrideValue?: any) => {
      onConfirm(overrideValue)
    },
    [onConfirm]
  )

  const handleCancel = useCallback(() => {
    resetField(config.value)
    onCancel()
    onFieldEdit?.(false)
  }, [config.value, resetField, onCancel, onFieldEdit])

  const handleValueChange = useCallback(
    (newValue: any) => {
      updateTempValue(newValue, config.value)
      onValueChange(newValue)
    },
    [config.value, updateTempValue, onValueChange]
  )

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        // Rich text: Enter is a paragraph break inside the editor, never a
        // confirm. The field commits on blur or through the confirm control.
        if (config.type === FieldTypeEnum.RICHTEXT) {
          return
        }

        if (config.type === FieldTypeEnum.TEXTAREA) {
          if (e.shiftKey) {
            return
          }
          e.preventDefault()
          handleConfirm()
          return
        }

        if (
          config.type === FieldTypeEnum.SELECT ||
          config.type === FieldTypeEnum.COMPANY ||
          config.type === FieldTypeEnum.COUNTRY ||
          config.type === FieldTypeEnum.CITY ||
          config.type === FieldTypeEnum.CONTACT
        ) {
          const target = e.target as HTMLElement
          const selectWrapper = target.closest('.ant-select')
          const isDropdownOpen =
            selectWrapper?.classList.contains('ant-select-open') ||
            document.querySelector('.ant-select-dropdown:not(.ant-select-dropdown-hidden)')

          if (isDropdownOpen) {
            return
          }

          e.preventDefault()
          handleConfirm()
        } else {
          e.preventDefault()
          handleConfirm()
        }
      } else if (e.key === 'Escape') {
        e.preventDefault()
        handleCancel()
      } else if (e.key === 'Tab') {
        // In 'edit' mode, allow default tab navigation between all fields
        if (inputMode === 'edit') {
          return // Let browser handle tab navigation normally
        }

        // In 'read' mode, use custom tab handling for inline editing
        e.preventDefault()

        const currentElement = e.target as HTMLElement
        const container = currentElement.closest('.inline-edit-container')

        if (container) {
          const focusableElements = container.querySelectorAll(
            'input, select, textarea, button[tabindex], [tabindex]:not([tabindex="-1"])'
          ) as NodeListOf<HTMLElement>

          const currentIndex = Array.from(focusableElements).indexOf(currentElement)
          const nextIndex = e.shiftKey ? currentIndex - 1 : currentIndex + 1

          if (nextIndex >= 0 && nextIndex < focusableElements.length) {
            focusableElements[nextIndex].focus()
          } else if (nextIndex < 0) {
            focusableElements[focusableElements.length - 1].focus()
          } else if (nextIndex >= focusableElements.length) {
            focusableElements[0].focus()
          }
        }
      }
    },
    [config.type, handleConfirm, handleCancel, inputMode]
  )

  const handleBlurInternal = useCallback(() => {
    if (inputMode === 'edit') {
      onBlur?.()
      return
    }

    // Check if this is a clearing action (empty string/null when original had a value)
    // For select fields, clearing should not show popconfirm when blurring - keep field in edit mode
    // Original value can be an object (table grid) or a string (forms)
    const isClearingSelect =
      (tempValue === '' || tempValue === null || tempValue === undefined) &&
      config.value !== '' &&
      config.value !== null &&
      config.value !== undefined &&
      (config.type === FieldTypeEnum.SELECT ||
        config.type === FieldTypeEnum.COUNTRY ||
        config.type === FieldTypeEnum.CITY ||
        config.type === FieldTypeEnum.COMPANY ||
        config.type === FieldTypeEnum.DEAL ||
        config.type === FieldTypeEnum.PROSPECT ||
        config.type === FieldTypeEnum.CONTACT)

    if (hasChanges()) {
      if (isClearingSelect) {
        // Clearing a lookup is a destructive write (a delink) — click-away
        // asks first instead of saving silently (owner decision 2026-08-26).
        setShowPopconfirm(true)
      } else {
        // Save-on-click-away is the default for entry fields (owner decision
        // 2026-08-26): a value the operator entered is kept, not discarded —
        // the old "unsaved changes" popconfirm only appears for the
        // destructive clear above. Validation/confirmation gates inside
        // handleConfirm (required fields, confirmPatch) still apply.
        handleConfirm()
      }
    } else {
      handleCancel()
    }
  }, [hasChanges, handleCancel, handleConfirm, inputMode, onBlur, tempValue, config.value, config.type, setShowPopconfirm])

  const handlePopconfirmConfirm = useCallback(() => {
    setShowPopconfirm(false)
    handleConfirm()
  }, [handleConfirm])

  const handlePopconfirmCancel = useCallback(() => {
    setShowPopconfirm(false)
    handleCancel()
  }, [handleCancel])

  return {
    handleStartEdit,
    handleConfirm,
    handleCancel,
    handleValueChange,
    handleKeyPress,
    handleBlurInternal,
    handlePopconfirmConfirm,
    handlePopconfirmCancel
  }
}
